/**
 * Календарь выкладки сети (задачи Оли №385, №388): окно «Когда выложить в ‹Сеть›» в блоке утверждения —
 * сетка месяца (как календарь браузера, но с отметками), у дней с уже запланированными постами этой сети
 * точка и число; черновики тоже, чтобы видеть пересечения до утверждения. Данные —
 * `GET /api/posts/calendar?network=…` (`contentplan.calendar`). Даты без часового пояса, как их ввели, —
 * дни собираются из строк.
 * Спека `docs/superpowers/specs/2026-10-22-posts-schedule-calendar-design.md`.
 */

export type CalendarItem = {
  plan_id: number;
  publish_date: string;
  status: "draft" | "approved";
  variant_id: number;
  asset_path: string;
  video_ms: number | null;
  poster_path: string | null;
};

export type Month = { year: number; month: number };
export type GridDay = { day: string; date: number; inMonth: boolean };

const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
]; // prettier-ignore
const pad = (n: number) => String(n).padStart(2, "0");

export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Недели месяца с понедельника; дни соседних месяцев добивают крайние недели (`inMonth: false`). */
export function monthGrid(year: number, month: number): GridDay[][] {
  const first = new Date(year, month - 1, 1);
  const shift = (first.getDay() + 6) % 7; // пн = 0
  const days = new Date(year, month, 0).getDate();
  const total = Math.ceil((shift + days) / 7) * 7;
  const weeks: GridDay[][] = [];
  for (let i = 0; i < total; i += 1) {
    const d = new Date(year, month - 1, 1 - shift + i);
    if (i % 7 === 0) weeks.push([]);
    weeks[weeks.length - 1]?.push({ day: dayKey(d), date: d.getDate(), inMonth: d.getMonth() === month - 1 });
  }
  return weeks;
}

/** Посты по дням, внутри дня — по времени. */
export function byDay(items: CalendarItem[]): Map<string, CalendarItem[]> {
  const out = new Map<string, CalendarItem[]>();
  for (const it of [...items].sort((a, b) => a.publish_date.localeCompare(b.publish_date))) {
    const key = it.publish_date.slice(0, 10);
    out.set(key, [...(out.get(key) ?? []), it]);
  }
  return out;
}

export function shiftMonth(m: Month, by: number): Month {
  const d = new Date(m.year, m.month - 1 + by, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function monthTitle(m: Month): string {
  return `${MONTHS[m.month - 1]} ${m.year}`;
}

/** День и время для окна: из даты выкладки, без неё — сегодня и 12:00. */
export function splitWhen(iso: string | null, today: Date = new Date()): { day: string; time: string } {
  if (!iso) return { day: dayKey(today), time: "12:00" };
  return { day: iso.slice(0, 10), time: iso.slice(11, 16) || "12:00" };
}
